import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';
import { ModalRatingAndNumReviews } from '../ModalRatingAndNumReviews';

export const CloudCareDuoModalContent = ({
  modalCard,
}: {
  modalCard: Card;
}) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modal.title}
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <ModalRatingAndNumReviews modalCard={modalCard} />
      <Typography className="cur-mt-4">
        The anchor for your daily skincare ritual, this rich-yet-weightless duo
        deeply hydrates with a light-as-air finish. Nurture your skin’s glow
        with luxuriously whipped textures—every day.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>Cream Oil Cleanser (40 mL):</strong> A makeup-melting,
            glow-restoring cream oil wash. Leaves your freshly-cleansed skin
            smooth, soft, and hydrated.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>Weightless Whipped Moisturizer (25 mL):</strong> A
            moisture-rich cream with a light-as-air finish, nourishing your skin
            for all-day hydration.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Use morning and night in tandem with your customized Agency Future
        and/or Dark Spot Formulas for an effortless daily care ritual, suited
        specifically to your skin.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>Cream Oil Cleanser:</strong> Gently massage into damp skin,
            then rinse off with water.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>Weightless Whipped Moisturizer:</strong> Gently massage into
            face and neck after cleansing, morning and night.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        Featured ingredients
      </Typography>
      <Typography className="cur-mt-4" weight="bold">
        Cream Oil Cleanser
      </Typography>
      <IngredientsAccordion
        body="Helps remove impurities and makeup while nourishing the skin"
        title="Squalane"
      />
      <IngredientsAccordion
        body="Locks water in the skin’s outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <Typography className="cur-mt-4" weight="bold">
        Weightless Whipped Moisturizer
      </Typography>
      <IngredientsAccordion
        body="Seals in moisture to soften and smooth skin"
        title="Shea butter"
      />
      <IngredientsAccordion
        body="Attracts and holds water to deeply hydrate and plump the skin"
        title="Hyaluronic acid"
      />
      <IngredientsAccordion
        body="Locks water in the skin’s outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <IngredientsAccordion
        body="Fortify the moisture barrier to keep skin hydrated"
        title="Ceramides"
      />
      <Typography className="cur-mt-4" size="h3">
        Full ingredients list
      </Typography>
      <Typography className="cur-mt-4">
        <strong>Cream Oil Cleanser:</strong> Water, Cocamidopropyl Betaine,
        Cetearyl Alcohol, Sodium Methyl Cocoyl Taurate, Caprylic/Capric
        Triglyceride, Sodium Chloride, Glycerin, Squalane, Acrylates/C10-30
        Alkyl Acrylate Crosspolymer, Styrene/Acrylates Copolymer,
        Leuconostoc/Radish Root Ferment Filtrate, Hydrogenated Lecithin, Coconut
        Acid, Caesalpinia Spinosa Gum, Phenoxyethanol, Ethylhexylglycerin,
        Sodium Hydroxide
      </Typography>
      <Typography className="cur-mt-4">
        <strong>Weightless Whipped Moisturizer:</strong> Water, Glycerin,
        Ethylhexyl Salicylate, Hydrogenated Polyisobutene, Dimethicone,
        Butyrospermum Parkii (Shea) Butter, Butylene Glycol, Propanediol,
        Cetearyl Alcohol, Niacinamide, Sodium Hyaluronate, Ceramide NP, Ceramide
        AP, Ceramide EOP, Panthenol, Avena Sativa (Oat) Kernel Flour,
        Phytosphingosine, Cholesterol, Sodium Lauroyl Lactylate,
        Acrylates/C10-30 Alkyl Acrylate Crosspolymer, Hydrogenated Lecithin,
        Xanthan Gum, Tocopherol, Carbomer, Benzyl Alcohol, Sodium Hydroxide
      </Typography>
    </>
  );
};
